/* lang-ipa.js — English pronunciation engine for Timbrica language tools.
 *
 * LangIPA: CMU Pronouncing Dictionary lookup → IPA, with maximal-onset
 * syllabification, stress marks, and a rule-based g2p fallback for words
 * not in the dictionary (flagged `approximate`).
 *
 * Plain IIFE, no dependencies. Exposes window.LangIPA and module.exports.
 * Datasets: /data/lang/ipa/cmudict-{a..z,_}.json (+ cmudict-manifest.json).
 */
(function (root, factory) {
  'use strict';
  var LangIPA = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = LangIPA;
  if (typeof window !== 'undefined') window.LangIPA = LangIPA;
  else if (root) root.LangIPA = LangIPA;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ── ARPABET → IPA (American English) ─────────────────────────── */
  var VOWELS = {
    AA: 'ɑ', AE: 'æ', AH: 'ʌ', AO: 'ɔ', AW: 'aʊ', AY: 'aɪ',
    EH: 'ɛ', ER: 'ɝ', EY: 'eɪ', IH: 'ɪ', IY: 'i', OW: 'oʊ',
    OY: 'ɔɪ', UH: 'ʊ', UW: 'u'
  };
  var VOWEL_REDUCED = { AH: 'ə', ER: 'ɚ' }; // unstressed (digit 0)

  /* ── British (RP / General British) realisation ───────────────── */
  /* Same ARPABET phonemes, different realisation. The systematic
   * differences (non-rhotic coda R, GOAT əʊ, NURSE ɜː, length marks,
   * reduced ə) are always correct. Three vowels split lexically in a way
   * CMUdict cannot encode (LOT/PALM for AA, CLOTH/THOUGHT for AO,
   * TRAP/BATH for AE) — handled by the curated word sets below; unlisted
   * words take the majority default (ɒ / ɔː / æ). */
  var VOWELS_GB = {
    AA: 'ɒ', AE: 'æ', AH: 'ʌ', AO: 'ɔː', AW: 'aʊ', AY: 'aɪ',
    EH: 'e', ER: 'ɜː', EY: 'eɪ', IH: 'ɪ', IY: 'iː', OW: 'əʊ',
    OY: 'ɔɪ', UH: 'ʊ', UW: 'uː',
    // pseudo R-coloured vowels, produced by gbRhotic() from vowel + coda R
    AAR: 'ɑː', AOR: 'ɔː', AER: 'eə', EHR: 'eə', IHR: 'ɪə', IYR: 'ɪə',
    EYR: 'eə', UHR: 'ʊə', UWR: 'ʊə', OWR: 'ɔː', AWR: 'aʊə', AYR: 'aɪə', OYR: 'ɔɪə',
    AHR: 'ɜː', ERR: 'ɜː',
    // happY / thank yOU: the unstressed close vowels are SHORT at the end of a
    // word and before another vowel in modern GB — /ˈhæpi/, not /ˈhæpiː/.
    // Produced by gbHappy() below.
    IYH: 'i', UWH: 'u'
  };
  var VOWEL_REDUCED_GB = { AH: 'ə', ER: 'ə' };
  var GB_PSEUDO = strSet('AAR AOR AER EHR IHR IYR EYR UHR UWR OWR AWR AYR OYR AHR ERR IYH UWH'.split(' '));
  // AA or AE realised /ɑː/ in GB (PALM + BATH lexical sets — common words).
  var GB_BROAD_A = strSet((
    'father rather palm palms calm calmer calmly balm psalm psalms alms halve halves ' +
    'spa spas bra bras ma pa aha drama dramas llama llamas lava pasta mafia sonata facade facades ' +
    'bath baths path paths aftermath after afternoon afterwards afterward laugh laughs laughed laughing laughter ' +
    'draught draughts staff staffs half calf calves behalf ' +
    'class classes classroom glass glasses grass pass passes passed passing brass ' +
    'ask asks asked asking task tasks mask masks masked basket baskets rascal ' +
    'cast casts casting castle castles fasten past pastor plaster master masters mastered disaster disasters nasty ghastly ' +
    'fast faster fastest last lasts lasted lasting blast blasts vast ' +
    'dance dances danced dancer dancing chance chances france glance glances advance advances advanced advantage stance ' +
    'plant plants planted grant grants granted slant chant chants aunt aunts ' +
    'command commands demand demands demanded reprimand ' +
    'sample samples example examples branch branches ranch answer answers answered ' +
    'banana bananas ' +
    // spelled with a, said with the PALM vowel in Britain too (grandma /ˈɡrænmɑː/)
    'mama mamas mamma papa papas grandma grandmas grandpa grandpas pajamas pyjamas saga sagas lager lagers karma sauna saunas karate'
  ).split(/\s+/));
  // AO realised /ɒ/ in GB (CLOTH lexical set — otherwise AO → /ɔː/ THOUGHT).
  var GB_CLOTH = strSet((
    'dog dogs log logs fog fogs off offer offers offered office offices officer officers coffee ' +
    'cough coughs cross crosses crossing across loss losses lost boss bosses cost costs costing frost ' +
    'moss floss gloss glossy toss tosses long longer longest song songs wrong strong stronger belong belongs ' +
    'along gong throng prolong gone cloth broth moth soft soften softer often oft doff scoff ' +
    'because cos'
  ).split(/\s+/));
  // AH (STRUT) in CMUdict, LOT in GB: American /wʌt/, British /wɒt/.
  var GB_STRUT_LOT = strSet("what whats what's whatever whatsoever somewhat".split(' '));

  var CONS = {
    B: 'b', CH: 'tʃ', D: 'd', DH: 'ð', F: 'f', G: 'ɡ', HH: 'h',
    JH: 'dʒ', K: 'k', L: 'l', M: 'm', N: 'n', NG: 'ŋ', P: 'p',
    R: 'ɹ', S: 's', SH: 'ʃ', T: 't', TH: 'θ', V: 'v', W: 'w',
    Y: 'j', Z: 'z', ZH: 'ʒ'
  };

  /* Legal syllable onsets (ARPABET, space-joined). Used by the
   * maximal-onset syllabifier. Single consonants are legal except NG. */
  var ONSET2 = strSet([
    'P L', 'P R', 'B L', 'B R', 'T R', 'D R', 'K L', 'K R', 'G L', 'G R',
    'F L', 'F R', 'TH R', 'SH R', 'T W', 'D W', 'K W', 'G W', 'TH W', 'HH W',
    'S P', 'S T', 'S K', 'S L', 'S M', 'S N', 'S W', 'S F',
    'P Y', 'B Y', 'T Y', 'D Y', 'K Y', 'G Y', 'F Y', 'V Y', 'M Y', 'N Y',
    'HH Y', 'L Y', 'S Y', 'TH Y'
  ]);
  var ONSET3 = strSet(['S P L', 'S P R', 'S T R', 'S K R', 'S K W', 'S P Y']);

  function strSet(arr) { var s = {}; for (var i = 0; i < arr.length; i++) s[arr[i]] = 1; return s; }
  function base(ph) { return ph.replace(/[0-2]$/, ''); }
  function stressOf(ph) { var m = ph.match(/([0-2])$/); return m ? +m[1] : -1; }
  function isVowelBase(b) { return VOWELS.hasOwnProperty(b) || GB_PSEUDO.hasOwnProperty(b); }

  /* Map one ARPABET phone (base, no digit) to IPA. opts.accent 'gb' selects
   * the British realisation; opts.broadA forces AA/AE → ɑː (BATH/PALM word). */
  function phoneToIPA(b, stress, opts) {
    opts = opts || {};
    if (opts.accent === 'gb') {
      if (GB_PSEUDO.hasOwnProperty(b)) return VOWELS_GB[b];
      if (VOWELS.hasOwnProperty(b)) {
        if (stress === 0 && VOWEL_REDUCED_GB[b]) return VOWEL_REDUCED_GB[b];
        if ((b === 'AA' || b === 'AE') && opts.broadA) return 'ɑː';
        if (b === 'AO' && opts.cloth) return 'ɒ';
        return VOWELS_GB[b] || VOWELS[b];
      }
      if (b === 'R') return 'r';   // onset R — British learner convention uses plain /r/
      return CONS[b] || '';
    }
    if (VOWELS.hasOwnProperty(b)) {
      if (stress === 0 && VOWEL_REDUCED[b]) return VOWEL_REDUCED[b];
      return VOWELS[b];
    }
    if (b === 'R' && opts.simpleR) return 'r';
    return CONS[b] || '';
  }

  /* NURSE + R + vowel is /ɜːr/ only where the word is built on a stem that
   * ends in the NURSE vowel (fur → furry, occur → occurring). Everywhere else
   * a stressed ER before a vowel is the STRUT vowel plus a linking r: hurry
   * /ˈhʌri/, worry /ˈwʌri/, courage /ˈkʌrɪdʒ/, thorough /ˈθʌrə/ — a lexical
   * split CMUdict cannot encode, because General American merged it. */
  var GB_NURSE_R = strSet((
    'furry furrier furriest blurry blurrier stirring stirrer purring whirring spurring slurring ' +
    'referring referral referrals occurring recurring preferring deferring deferral conferring inferring ' +
    'transferring deterring interring demurring concurring incurring erring'
  ).split(/\s+/));
  // Final unstressed OW in -ough is the schwa in RP: thorough /ˈθʌrə/, borough /ˈbʌrə/.
  var GB_OUGH_SCHWA = strSet('thorough thoroughly thoroughness borough boroughs scarborough marlborough'.split(' '));

  /* Non-rhotic pre-pass: merge a vowel + coda R into a single pseudo-vowel
   * (car K AA R → K AAR → /kɑː/) and drop any other coda R; onset R (before a
   * vowel — the linking case) stays. Runs only for the British accent.
   *
   * ER is the r-coloured vowel itself, so before another vowel its r is the
   * onset of the next syllable and must SURFACE: different /ˈdɪfərənt/,
   * camera /ˈkæmərə/, every /ˈevəri/, hurry /ˈhʌri/. Until 2026-09-24 ER was
   * passed through whole and rendered as a bare /ə/ or /ɜː/, which dropped
   * the r in all of those — /ˈdɪfəənt/, /ˈkæməə/, /ˈevəi/, /ˈhɜːi/ — on every
   * British transcription the site printed (UK is the default variant). */
  function gbRhotic(arpa, word) {
    var out = [], i = 0;
    while (i < arpa.length) {
      var tok = arpa[i], b = base(tok), m = tok.match(/([0-2])$/), st = m ? m[1] : '';
      if (b === 'ER') {
        var nx = (i + 1 < arpa.length) ? base(arpa[i + 1]) : null;
        if (nx !== null && isVowelBase(nx)) {
          if (st === '0') out.push('AH0');
          else if (word && GB_NURSE_R.hasOwnProperty(word)) out.push(tok);
          else out.push('AH' + st);
          out.push('R');
          i++; continue;
        }
        out.push(tok); i++;
      } else if (VOWELS.hasOwnProperty(b)) {
        if (i + 1 < arpa.length && base(arpa[i + 1]) === 'R') {
          var afterR = (i + 2 < arpa.length) ? base(arpa[i + 2]) : null;
          if (afterR === null || !isVowelBase(afterR)) { out.push(b + 'R' + st); i += 2; continue; }
        }
        out.push(tok); i++;
      } else if (b === 'R') {
        var nxt = (i + 1 < arpa.length) ? base(arpa[i + 1]) : null;
        if (nxt === null || !isVowelBase(nxt)) { i++; continue; }  // drop coda R
        out.push(tok); i++;
      } else { out.push(tok); i++; }
    }
    return out;
  }

  /* Yod after t, d, n, th: RP keeps /j/ before the GOOSE and CURE vowels where
   * the spelling has u / ew / eu / ue — new /njuː/, tune /tjuːn/, duty
   * /ˈdjuːti/, student /ˈstjuːdənt/, during /ˈdjʊərɪŋ/. CMUdict is American
   * and drops it (yod-dropping), so the British renderer printed the American
   * /nuː/ /tuːn/ — the one difference a Russian or Ukrainian school teaches
   * first («нью», «тьюн»). The spelling test keeps do, noon, two, tool, too
   * yod-less: their vowel is written o / oo. */
  var YOD_CONS = { T: 1, D: 1, N: 1, TH: 1 };
  var YOD_SPELL = /(?:t|d|n|th)(?:u|ew|eu|ue)/;
  function gbYod(arpa, word) {
    if (!word || !YOD_SPELL.test(word)) return arpa;
    var out = [];
    for (var i = 0; i < arpa.length; i++) {
      out.push(arpa[i]);
      if (YOD_CONS[base(arpa[i])] && i + 1 < arpa.length) {
        var nb = base(arpa[i + 1]);
        if (nb === 'UW' || nb === 'UH' || nb === 'UWR' || nb === 'UHR') out.push('Y');
      }
    }
    return out;
  }

  function gbLot(arpa, word) {
    if (!word || !GB_STRUT_LOT.hasOwnProperty(word)) return arpa;
    var out = arpa.slice();
    for (var i = 0; i < out.length; i++) {
      if (base(out[i]) === 'AH' && /[12]$/.test(out[i])) { out[i] = 'AA' + out[i].slice(-1); break; }
    }
    return out;
  }

  function gbOugh(arpa, word) {
    if (!word || !GB_OUGH_SCHWA.hasOwnProperty(word)) return arpa;
    var out = arpa.slice();
    // CMUdict writes the last vowel OW0 in thorough but OW2 in borough.
    for (var i = out.length - 1; i >= 0; i--) {
      if (base(out[i]) === 'OW') { out[i] = 'AH0'; break; }
    }
    return out;
  }

  /* happY tensing: an UNSTRESSED close front vowel is short at the end of a
   * word (happy, coffee, city, very, money) and before another vowel
   * (immediately, various). Mapping IY→/iː/ unconditionally, as the table
   * does, mis-transcribed every one of those — several thousand of the
   * commonest words in English — as /ˈhæpiː/. The same holds for /u/ before a
   * vowel: influence /ˈɪnfluəns/, situation /ˌsɪtʃuˈeɪʃən/.
   *
   * Deliberately NOT applied to unstressed IY elsewhere: the "re-" prefix
   * (recreate /ˌriːkriˈeɪt/) keeps its long vowel and CMUdict marks it 0 too,
   * so POSITION, not the stress digit alone, is what separates the two. */
  function gbHappy(arpa) {
    var out = arpa.slice();
    // A spelling guard for the stressed "-ee" suffix was tried and dropped:
    // of the 159 dictionary words that end in "-ee" with a final IY0, nearly
    // all are names where the happY vowel is CORRECT (Attlee, Banerjee,
    // bungee), so the guard broke far more than it saved.
    for (var i = 0; i < out.length; i++) {
      var b = base(out[i]), st = stressOf(out[i]);
      if (st === 1) continue;                       // a primary-stressed vowel is long
      var nxt = (i + 1 < out.length) ? base(out[i + 1]) : null;
      var preVowel = nxt !== null && isVowelBase(nxt);
      // Word-finally only an UNSTRESSED vowel shortens — final IY2 stays long
      // ("employee" /ˌemplɔɪˈiː/). Before another vowel /iː/ does not occur at
      // all, whatever digit CMUdict wrote ("immediately" IY2 → /i/).
      if (b === 'IY' && (preVowel || (nxt === null && st === 0))) out[i] = 'IYH';
      else if (b === 'UW' && preVowel) out[i] = 'UWH';
    }
    return out;
  }

  /* ── Syllabification (maximal-onset principle) ────────────────── */
  function isLegalOnset(cluster) {
    if (cluster.length === 0) return true;
    if (cluster.length === 1) return cluster[0] !== 'NG';
    var key = cluster.join(' ');
    if (cluster.length === 2) return !!ONSET2[key];
    if (cluster.length === 3) return !!ONSET3[key];
    return false;
  }

  /* arpa: array of ARPABET tokens (with stress digits).
   * Returns array of syllables, each an array of ARPABET tokens. */
  function syllabify(arpa) {
    if (!arpa || !arpa.length) return [];
    var vowelIdx = [];
    for (var i = 0; i < arpa.length; i++) {
      if (isVowelBase(base(arpa[i]))) vowelIdx.push(i);
    }
    if (vowelIdx.length <= 1) return [arpa.slice()];

    var starts = [0];
    for (var v = 0; v < vowelIdx.length - 1; v++) {
      var cStart = vowelIdx[v] + 1, cEnd = vowelIdx[v + 1];
      var cluster = [];
      for (var c = cStart; c < cEnd; c++) cluster.push(base(arpa[c]));
      // longest legal onset = suffix of the cluster
      var onsetLen = 0;
      for (var k = Math.min(cluster.length, 3); k >= 0; k--) {
        if (isLegalOnset(cluster.slice(cluster.length - k))) { onsetLen = k; break; }
      }
      starts.push(cEnd - onsetLen);
    }
    starts.push(arpa.length);

    var sylls = [];
    for (var s = 0; s < starts.length - 1; s++) sylls.push(arpa.slice(starts[s], starts[s + 1]));
    return sylls;
  }

  /* ── Assemble an IPA result from an ARPABET array ─────────────── */
  function assemble(word, arpa, source, opts) {
    opts = opts || {};
    var useArpa = arpa;
    if (opts.accent === 'gb') {
      var nw = normalize(word), o2 = {};
      useArpa = gbHappy(gbYod(gbRhotic(gbOugh(gbLot(arpa, nw), nw), nw), nw));
      for (var kk in opts) o2[kk] = opts[kk];
      o2.broadA = GB_BROAD_A.hasOwnProperty(nw);
      o2.cloth = GB_CLOTH.hasOwnProperty(nw);
      opts = o2;
    }
    var sylls = syllabify(useArpa);
    var sylIpa = [], sylStress = [], stressIndex = -1;

    for (var s = 0; s < sylls.length; s++) {
      var syl = sylls[s], st = 0, txt = '';
      for (var i = 0; i < syl.length; i++) {
        var b = base(syl[i]), sd = stressOf(syl[i]);
        if (isVowelBase(b) && sd > 0) st = sd;
        txt += phoneToIPA(b, sd, opts);
      }
      var mark = '';
      if (sylls.length > 1 || opts.markMonosyllable) {
        if (st === 1) mark = 'ˈ';
        else if (st === 2) mark = 'ˌ';
      }
      if (st === 1 && stressIndex < 0) stressIndex = s;
      sylIpa.push(mark + txt);
      sylStress.push(st);
    }
    return {
      word: word,
      arpabet: arpa.slice(),
      ipa: sylIpa.join(''),
      syllables: sylIpa,
      stress: sylStress,
      stressIndex: stressIndex,
      source: source,
      approximate: source === 'fallback'
    };
  }

  /* Pure mapping: ARPABET array → IPA string (no syllable stress logic). */
  function arpaToIPA(arpa, opts) {
    var out = '';
    for (var i = 0; i < arpa.length; i++) {
      var b = base(arpa[i]), sd = stressOf(arpa[i]);
      out += phoneToIPA(b, sd, opts || {});
    }
    return out;
  }

  /* ── Rule-based g2p fallback (out-of-vocabulary words) ────────── */
  /* Ordered longest-match spelling rules. Produces ARPABET; primary
   * stress is placed on the first vowel. Deliberately approximate. */
  var G2P = [
    ['tch', ['CH']], ['dge', ['JH']], ['igh', ['AY']], ['ough', ['AO']],
    ['augh', ['AO']], ['eigh', ['EY']], ['sch', ['S', 'K']],
    ['ch', ['CH']], ['ck', ['K']], ['sh', ['SH']], ['ph', ['F']],
    ['th', ['TH']], ['wh', ['W']], ['ng', ['NG']], ['qu', ['K', 'W']],
    ['gh', []], ['kn', ['N']], ['wr', ['R']], ['gn', ['N']],
    ['ee', ['IY']], ['ea', ['IY']], ['ie', ['IY']], ['ei', ['IY']],
    ['oo', ['UW']], ['ou', ['AW']], ['ow', ['AW']], ['oa', ['OW']],
    ['oi', ['OY']], ['oy', ['OY']], ['ai', ['EY']], ['ay', ['EY']],
    ['au', ['AO']], ['aw', ['AO']], ['ew', ['UW']], ['ar', ['AA', 'R']],
    ['or', ['AO', 'R']], ['er', ['ER']], ['ir', ['ER']], ['ur', ['ER']],
    ['a', ['AE']], ['e', ['EH']], ['i', ['IH']], ['o', ['AA']],
    ['u', ['AH']], ['y', ['IH']],
    ['b', ['B']], ['c', ['K']], ['d', ['D']], ['f', ['F']], ['g', ['G']],
    ['h', ['HH']], ['j', ['JH']], ['k', ['K']], ['l', ['L']], ['m', ['M']],
    ['n', ['N']], ['p', ['P']], ['r', ['R']], ['s', ['S']], ['t', ['T']],
    ['v', ['V']], ['w', ['W']], ['x', ['K', 'S']], ['z', ['Z']]
  ];

  function g2p(word, opts) {
    var w = String(word || '').toLowerCase().replace(/[^a-z]/g, '');
    if (!w) return assemble(word, [], 'fallback', opts || {});
    // 'c' before e/i/y → S ; 'g' before e/i/y left as G (conservative)
    var phones = [], i = 0;
    while (i < w.length) {
      var matched = null;
      for (var r = 0; r < G2P.length; r++) {
        var g = G2P[r][0];
        if (w.substr(i, g.length) === g) {
          var ph = G2P[r][1].slice();
          if (g === 'c' && /[eiy]/.test(w[i + 1] || '')) ph = ['S'];
          matched = { len: g.length, ph: ph };
          break;
        }
      }
      if (!matched) { i++; continue; }
      for (var p = 0; p < matched.ph.length; p++) phones.push(matched.ph[p]);
      i += matched.len;
    }
    // silent final 'e' heuristic: drop a trailing EH from a final 'e'
    if (/[^aeiou]e$/.test(w) && phones.length && phones[phones.length - 1] === 'EH') {
      phones.pop();
    }
    // place primary stress on the first vowel
    var done = false;
    for (var k = 0; k < phones.length; k++) {
      if (isVowelBase(phones[k])) { phones[k] += done ? '0' : '1'; done = true; }
    }
    return assemble(word, phones, 'fallback', opts || {});
  }

  /* ── Dictionary loading (lazy, sharded) ───────────────────────── */
  var _base = '/data/lang/ipa';
  var _manifest = null;
  var _dict = Object.create(null);          // word → "AH0 B AW1 T"
  var _shardLoaded = Object.create(null);   // letter → true
  var _shardPromise = Object.create(null);  // letter → Promise
  var _loadPromise = null;

  function load(opts) {
    if (opts && opts.base) _base = opts.base.replace(/\/$/, '');
    if (_loadPromise) return _loadPromise;
    _loadPromise = fetch(_base + '/cmudict-manifest.json')
      .then(function (r) { if (!r.ok) throw new Error('manifest ' + r.status); return r.json(); })
      .then(function (m) { _manifest = m; return true; })
      .catch(function (e) { _loadPromise = null; throw e; });
    return _loadPromise;
  }

  function shardKey(word) {
    var c = word.charAt(0);
    return /[a-z]/.test(c) ? c : '_';
  }

  function ensureShard(letter) {
    if (_shardLoaded[letter]) return Promise.resolve(true);
    if (_shardPromise[letter]) return _shardPromise[letter];
    var p = (_manifest ? Promise.resolve() : load()).then(function () {
      var fn = (_manifest && _manifest.shards && _manifest.shards[letter]);
      if (!fn) { _shardLoaded[letter] = true; return true; }
      return fetch(_base + '/' + fn).then(function (r) {
        if (!r.ok) throw new Error('shard ' + letter + ' ' + r.status);
        return r.json();
      }).then(function (obj) {
        for (var w in obj) _dict[w] = obj[w];
        _shardLoaded[letter] = true;
        return true;
      });
    }).catch(function (e) { _shardPromise[letter] = null; throw e; });
    _shardPromise[letter] = p;
    return p;
  }

  function normalize(word) {
    return String(word || '').toLowerCase().replace(/[^a-z']/g, '');
  }

  /* Synchronous lookup against already-loaded shards. */
  function lookupSync(word, opts) {
    var w = normalize(word);
    if (!w) return null;
    if (_dict[w]) return assemble(word, _dict[w].split(' '), 'cmudict', opts);
    // hyphen / OOV — handled by toIPA
    return null;
  }

  function toIPASync(word, opts) {
    var r = lookupSync(word, opts);
    return r || (normalize(word) ? g2p(word, opts) : null);
  }

  /* Async: ensures the needed shard is loaded, then resolves a result. */
  function toIPA(word, opts) {
    var w = normalize(word);
    if (!w) return Promise.resolve(null);
    var letters = [shardKey(w)];
    if (w.indexOf("'") >= 0) { /* same shard */ }
    return ensureShard(letters[0]).then(function () {
      var hit = lookupSync(word, opts);
      return hit || g2p(word, opts);
    }).catch(function () { return g2p(word); });
  }

  /* Tokenise text and transcribe every word. Resolves an array of
   * tokens: {token} for words gets {...IpaResult}; punctuation/space
   * tokens get {token, punct:true} / {token, space:true}. */
  function phrase(text, opts) {
    var raw = String(text || '');
    var toks = raw.match(/[A-Za-z']+|\s+|[^A-Za-z'\s]+/g) || [];
    var letters = {};
    for (var i = 0; i < toks.length; i++) {
      if (/[A-Za-z]/.test(toks[i])) letters[shardKey(normalize(toks[i]))] = 1;
    }
    var jobs = Object.keys(letters).map(function (l) { return ensureShard(l); });
    return Promise.all(jobs).then(function () {
      return toks.map(function (t) {
        if (/^\s+$/.test(t)) return { token: t, space: true };
        if (!/[A-Za-z]/.test(t)) return { token: t, punct: true };
        var res = lookupSync(t, opts) || g2p(t, opts);
        res.token = t;
        return res;
      });
    });
  }

  /* Optional high-accuracy phonemizer hook (e.g. eSpeak-NG WASM).
   * If set, replaces g2p for OOV words. fn(word) → IpaResult-like. */
  var _phonemizer = null;
  function setPhonemizer(fn) { _phonemizer = fn; }
  function g2pHook(word, opts) {
    if (_phonemizer) { try { return _phonemizer(word, opts); } catch (e) { /* fall through */ } }
    return g2p(word, opts);
  }

  /* Both accents from a single dictionary lookup. Resolves {us, gb} IpaResults. */
  function toIPADual(word) {
    var w = normalize(word);
    if (!w) return Promise.resolve(null);
    var both = function (arpa, source) {
      return arpa
        ? { us: assemble(word, arpa, source, { accent: 'us' }), gb: assemble(word, arpa, source, { accent: 'gb' }) }
        : { us: g2p(word, { accent: 'us' }), gb: g2p(word, { accent: 'gb' }) };
    };
    return ensureShard(shardKey(w))
      .then(function () { return both(_dict[w] ? _dict[w].split(' ') : null, 'cmudict'); })
      .catch(function () { return both(null); });
  }

  return {
    load: load,
    toIPA: toIPA,
    toIPADual: toIPADual,
    toIPASync: toIPASync,
    phrase: phrase,
    arpaToIPA: arpaToIPA,
    syllabify: syllabify,
    assemble: assemble,
    g2p: g2pHook,
    setPhonemizer: setPhonemizer,
    has: function (word) { return !!_dict[normalize(word)]; },
    isVowel: isVowelBase,
    get ready() { return !!_manifest; },
    VOWELS: VOWELS, CONS: CONS
  };
});
